import React, { useEffect, useMemo, useState } from "react";
import "../../secao-bloqueio/SecaoBloqueioBase.css";

const initialForm = {
  motivo: "",
};

function normalizeCodeDraft(value, max = 160) {
  return String(value ?? "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .replace(/[<>]/g, "")
    .toLocaleUpperCase("pt-BR")
    .slice(0, max);
}

function sanitizeCode(value, max = 160) {
  return normalizeCodeDraft(value, max).trim();
}

function ToggleSwitch({ on = false, onToggle, title }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      className={`scb-toggle ${on ?"on" : "off"}`}
      onClick={onToggle}
      title={title || (on ?"Inativar" : "Ativar")}
    >
      <span className="scb-toggle-knob" />
    </button>
  );
}

function IconEdit(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function IconTrash(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

export default function MotivosBaseTemplate({
  titulo = "Motivos",
  descricao = "Cadastre os motivos usados pelas telas de interdição do CIM.",
  placeholder = "Ex.: ÁGUA SOBRE A VIA",
  hint = "Use esta base para centralizar os motivos do CIM.",
  listFn,
  createFn,
  updateFn,
  deleteFn,
  toggleFn,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [filtroTexto, setFiltroTexto] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);

  async function carregar() {
    setLoading(true);
    setError("");
    try {
      const ativo =
        filtroStatus === "ativos" ?1 : filtroStatus === "inativos" ?0 : undefined;
      const result = await listFn({
        q: filtroTexto,
        ativo,
        limit: 5000,
        offset: 0,
      });
      setRows(result.rows || []);
    } catch (err) {
      setRows([]);
      setError(err?.message || "Erro ao carregar a base de motivos.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    carregar();
  }, [filtroTexto, filtroStatus]);

  const collator = useMemo(
    () =>
      new Intl.Collator("pt-BR", {
        sensitivity: "base",
        numeric: true,
      }),
    []
  );

  const stats = useMemo(() => {
    const total = rows.length;
    const ativos = rows.filter((row) => Number(row?.ativo) === 1).length;
    return {
      total,
      ativos,
      inativos: total - ativos,
    };
  }, [rows]);

  const rowsOrdenadas = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const motivoA = String(a?.motivo || "");
        const motivoB = String(b?.motivo || "");
        const byMotivo = collator.compare(motivoA, motivoB);
        if (byMotivo !== 0) return byMotivo;
        return Number(a?.id || 0) - Number(b?.id || 0);
      }),
    [collator, rows]
  );

  function resetForm() {
    setForm(initialForm);
    setEditingId(null);
  }

  function handleChange(value) {
    setForm({ motivo: normalizeCodeDraft(value, 160) });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    setFeedback("");

    try {
      const payload = {
        motivo: sanitizeCode(form.motivo, 160),
      };

      if (!payload.motivo) {
        throw new Error("Preencha o motivo antes de salvar.");
      }

      if (editingId) {
        await updateFn(editingId, payload);
        setFeedback("Motivo atualizado com sucesso.");
      } else {
        await createFn(payload);
        setFeedback("Motivo cadastrado com sucesso.");
      }

      resetForm();
      await carregar();
    } catch (err) {
      setError(err?.message || "Não foi possível salvar o motivo.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(row) {
    if (!row?.id) return;
    const ok = window.confirm(`Excluir o motivo ${row?.motivo || "-"}?`);
    if (!ok) return;

    try {
      await deleteFn(row.id);
      setFeedback("Motivo excluído com sucesso.");
      if (editingId === row.id) resetForm();
      await carregar();
    } catch (err) {
      setError(err?.message || "Não foi possível excluir o motivo.");
    }
  }

  async function handleToggle(row) {
    if (!row?.id) return;
    try {
      await toggleFn(row.id, Number(row?.ativo) === 1 ?0 : 1);
      setFeedback("Status do motivo atualizado.");
      await carregar();
    } catch (err) {
      setError(err?.message || "Não foi possível alterar o status.");
    }
  }

  function handleEdit(row) {
    setEditingId(row?.id || null);
    setForm({
      motivo: row?.motivo != null ?String(row.motivo) : "",
    });
    setFeedback("");
    setError("");
  }

  return (
    <div className="scb-base">
      <div className="scb-cards">
        <div className="scb-card">
          <span>Total</span>
          <strong>{stats.total}</strong>
        </div>
        <div className="scb-card">
          <span>Ativos</span>
          <strong>{stats.ativos}</strong>
        </div>
        <div className="scb-card">
          <span>Inativos</span>
          <strong>{stats.inativos}</strong>
        </div>
      </div>

      <section className="scb-form-panel">
        <div className="scb-form-head">
          <div>
            <h4>{editingId ?`Editar ${titulo}` : `Cadastrar ${titulo}`}</h4>
            <p>{descricao}</p>
          </div>
          {editingId ?(
            <button type="button" className="scb-btn scb-btn--ghost" onClick={resetForm}>
              Novo cadastro
            </button>
          ) : null}
        </div>

        <form className="scb-form-grid" onSubmit={handleSubmit}>
          <label className="scb-field">
            <span>Motivo</span>
            <input
              type="text"
              value={form.motivo}
              onChange={(event) => handleChange(event.target.value)}
              placeholder={placeholder}
            />
          </label>

          <div className="scb-form-actions">
            <button type="submit" className="scb-btn scb-btn--primary" disabled={saving}>
              {saving ?"Salvando..." : editingId ?"Salvar alterações" : "Cadastrar motivo"}
            </button>
          </div>
        </form>

        <p className={`scb-feedback ${error ?"is-error" : ""}`}>{error || feedback || hint}</p>
      </section>

      <section className="scb-table-panel">
        <div className="scb-toolbar">
          <input
            type="text"
            value={filtroTexto}
            onChange={(event) => setFiltroTexto(event.target.value)}
            placeholder="Buscar motivo..."
          />

          <select value={filtroStatus} onChange={(event) => setFiltroStatus(event.target.value)}>
            <option value="todos">Todos os status</option>
            <option value="ativos">Somente ativos</option>
            <option value="inativos">Somente inativos</option>
          </select>
        </div>

        <div className="scb-table-wrap">
          <table className="scb-table scb-table--head">
            <colgroup>
              <col style={{ width: "72%" }} />
              <col style={{ width: "28%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Motivo</th>
                <th>Controles</th>
              </tr>
            </thead>
          </table>

          <div className="scb-table-scroll">
            <table className="scb-table scb-table--body">
              <colgroup>
                <col style={{ width: "72%" }} />
                <col style={{ width: "28%" }} />
              </colgroup>
              <tbody>
                {loading ?(
                  <tr>
                    <td colSpan={2} className="scb-empty">
                      Carregando base de motivos...
                    </td>
                  </tr>
                ) : !rowsOrdenadas.length ?(
                  <tr>
                    <td colSpan={2} className="scb-empty">
                      Nenhum motivo encontrado.
                    </td>
                  </tr>
                ) : (
                  rowsOrdenadas.map((row) => {
                    const ativo = Number(row?.ativo) === 1;
                    return (
                      <tr key={row.id}>
                        <td>{row?.motivo || "-"}</td>
                        <td>
                          <div className="scb-actions">
                            <ToggleSwitch
                              on={ativo}
                              title={ativo ?"Inativar motivo" : "Ativar motivo"}
                              onToggle={() => handleToggle(row)}
                            />

                            <button
                              type="button"
                              className="scb-icon-btn"
                              title="Editar"
                              onClick={() => handleEdit(row)}
                            >
                              <IconEdit />
                            </button>

                            <button
                              type="button"
                              className="scb-icon-btn"
                              title="Excluir"
                              onClick={() => handleDelete(row)}
                            >
                              <IconTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
